// RecallControl — "Recall" button (top of the Home hamburger menu) that brings
// back a signed-in user's recent conversations on a silo (Public Q&A OR the AI
// Assistant), on any device. Jun 2026 (Randy, Option C):
//   • 1 tap  → load the MOST RECENT conversation (audio recognized).
//   • tap again WITHIN 5s → step to the 2nd … up to the 5th most recent.
//   • a 6th tap within 5s loops back to #1 (only ever the last 5).
//   • after 5s of no taps, the next tap resets to the most recent (#1).
// Recalling refreshes the conversation's 30-day text clock AND its 24h audio
// cache (via the caller's `touch`), so a recalled thread plays free again.
import { useRef, useState } from "react";
import { ClockCounterClockwise, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const relTime = (iso) => {
  if (!iso) return "";
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return "just now";
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
};

export function RecallControl({ fetchRecent, onLoad, touch, testidPrefix = "recall", variant = "mobile", onRecallTap }) {
  const [tip, setTip] = useState(false);
  const [status, setStatus] = useState("");
  const recentRef = useRef([]);
  const idxRef = useRef(-1);
  const lastTapRef = useRef(0);
  const busyRef = useRef(false);
  const statusTimer = useRef(null);

  const flash = (msg) => {
    setStatus(msg);
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(""), 4000);
  };

  const doRecall = async (e) => {
    e?.stopPropagation?.();
    onRecallTap?.();   // tells the menu to collapse to ONLY Recall during the cycle
    if (busyRef.current) return;
    const now = Date.now();
    if (!recentRef.current.length || now - lastTapRef.current > 5000) {
      // (Re)load the list when empty OR after the 5s window so it's always fresh.
      busyRef.current = true;
      try {
        const r = await fetchRecent();
        recentRef.current = Array.isArray(r) ? r : (r?.sessions || []);
      } catch { recentRef.current = []; }
      busyRef.current = false;
    }
    const list = recentRef.current;
    if (!list.length) { toast("No past conversations to recall here yet."); return; }
    const span = Math.min(5, list.length);
    if (now - lastTapRef.current > 5000) idxRef.current = 0;
    else idxRef.current = (idxRef.current + 1) % span;
    lastTapRef.current = now;
    const sess = list[idxRef.current];
    try { onLoad?.(sess); } catch { /* */ }
    try { touch?.(sess); } catch { /* */ }
    flash(`#${idxRef.current + 1} of ${span}${sess.updated_at ? " · " + relTime(sess.updated_at) : ""}`);
  };

  const eye = (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setTip((v) => !v); }}
        data-testid={`${testidPrefix}-info`}
        aria-label="What does Recall do?"
        className="opacity-50 hover:opacity-100 transition-opacity p-0.5"
        title="What does Recall do?"
      >
        <Eye size={11} weight="duotone" />
      </button>
      {tip && (
        <span
          role="tooltip"
          data-testid={`${testidPrefix}-tooltip`}
          onClick={(e) => e.stopPropagation()}
          className="absolute left-2 top-4 z-[200] w-56 rounded-lg bg-[#1C1B1A] text-[#FDFCF8] text-[11px] leading-snug p-2.5 shadow-xl text-left normal-case"
        >
          Tap <b>Recall</b> to reopen your last conversation. Tap again within 5 seconds to step back through your last 5 — it loops, and after 5 seconds it returns to the most recent. Recalling also refreshes the audio so it plays free again.
        </span>
      )}
    </span>
  );

  if (variant === "desktop") {
    return (
      <div className="relative hidden sm:flex flex-col shrink-0">
        <div className="flex items-center gap-1">
          <button
            type="button" onClick={doRecall} data-testid={`${testidPrefix}-btn`}
            className="text-[11px] tpl-muted hover:tpl-accent flex items-center gap-1 px-2 py-1 rounded-md border tpl-border tpl-surface shrink-0"
            title="Recall your recent conversations"
          >
            <ClockCounterClockwise size={12} weight="duotone" /> Recall
          </button>
          {eye}
        </div>
        {status && (
          <span className="absolute top-full mt-1 left-0 text-[10px] tpl-muted whitespace-nowrap" data-testid={`${testidPrefix}-status`}>
            ↩ {status}
          </span>
        )}
      </div>
    );
  }

  // Mobile dropdown row (sits at the very top of the hamburger menu).
  return (
    <div className="border-b tpl-border">
      <div className="w-full px-3 py-2 text-[13px] tpl-muted flex items-center gap-2">
        <button
          type="button" onClick={doRecall} data-testid={`${testidPrefix}-btn`}
          className="flex items-center gap-2 hover:tpl-accent flex-1 text-left"
        >
          <ClockCounterClockwise size={14} weight="duotone" /> Recall
        </button>
        {eye}
      </div>
      {status && (
        <div className="px-3 pb-1.5 -mt-1 text-[10px] tpl-muted" data-testid={`${testidPrefix}-status`}>↩ {status}</div>
      )}
    </div>
  );
}

export default RecallControl;
